import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

// Cloudflare Turnstile — explicit-render integration.
//
// Lightweight wrapper around the Turnstile JS API. It is deliberately
// self-contained (no extra dependency): the script is injected once and shared
// across mounts, the widget is rendered explicitly into our own container, and
// it is fully torn down on unmount so a re-mounted step never leaks an orphan
// widget. Token lifecycle is surfaced through callbacks:
//   onVerify(token)  — a fresh token is available
//   onExpire()       — the token expired; the parent must block submit again
//   onError()        — challenge failed; same handling as expiry
//
// `sitekey` is a PUBLIC key, safe to ship to the browser.

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ID = 'cf-turnstile-script';

// Resolves once window.turnstile is ready. The <script> is injected at most once
// regardless of how many widgets mount; concurrent callers share the same load.
const loadTurnstileScript = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const onReady = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('turnstile script loaded without global'));
    };

    if (existing) {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        existing.addEventListener('load', onReady, { once: true });
        existing.addEventListener('error', () => reject(new Error('turnstile script error')), {
          once: true,
        });
      }
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onReady, { once: true });
    script.addEventListener('error', () => reject(new Error('turnstile script error')), {
      once: true,
    });
    document.head.appendChild(script);
  });
};

const TurnstileWidget = ({ sitekey, onVerify, onExpire, onError, className, theme }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Keep the latest callbacks in a ref so the effect can stay keyed on `sitekey`
  // only — we must NOT re-render the widget every time the parent re-renders.
  const handlersRef = useRef({ onVerify, onExpire, onError });
  handlersRef.current = { onVerify, onExpire, onError };

  useEffect(() => {
    let cancelled = false;

    if (!sitekey) return undefined;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey,
          theme: theme || 'auto',
          callback: (token) => handlersRef.current.onVerify?.(token),
          'expired-callback': () => handlersRef.current.onExpire?.(),
          'error-callback': () => handlersRef.current.onError?.(),
        });
      })
      .catch(() => {
        if (!cancelled) handlersRef.current.onError?.();
      });

    return () => {
      cancelled = true;
      // Best-effort teardown — remove() unbinds and clears the rendered widget.
      if (window.turnstile && widgetIdRef.current != null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
    };
    // Intentionally keyed on sitekey only; handlers are read via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitekey]);

  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />;
};

TurnstileWidget.propTypes = {
  sitekey: PropTypes.string,
  onVerify: PropTypes.func,
  onExpire: PropTypes.func,
  onError: PropTypes.func,
  className: PropTypes.string,
  theme: PropTypes.oneOf(['auto', 'light', 'dark']),
};

export default TurnstileWidget;
